import { SUPPORTED_EXCHANGE } from '@/lib/configs/exchange-config'
import { exchangeToTradeVenue, getExchangeLabel } from '@/lib/configs/exchange-registry'
import { toExchangeEnum, tradeVenueToExchangeKey } from '@/lib/proto/util'
import { AccountRole, AccountStatus, PublicAccount } from '@/types/generated/common/account'
import { Exchange, TradeVenue } from '@/types/generated/common/types'

// A missing/garbage createdAt must never win the "oldest account" default by
// accident — sort it as infinitely NEW (last), not as epoch-0 (which would make it
// look oldest and win every tie).
const parseCreatedAt = (value: string): number => {
  const parsed = Number(value)
  return Number.isFinite(parsed) ? parsed : Infinity
}

/**
 * The venue's stable default account: oldest `createdAt` first, then `accountId`
 * ascending. Deliberately NOT `updatedAt` (the previous "most recent" heuristic) —
 * a rename or a reconnect bumps `updatedAt` for reasons that have nothing to do with
 * order intent, so that default silently drifted. `createdAt` never changes after
 * account creation, so once resolved for a venue, this pick is stable for as long as
 * the account set itself doesn't change.
 */
export function resolveDefaultAccountId<T extends { accountId: string; createdAt: string }>(
  accounts: T[]
): string | undefined {
  if (accounts.length === 0) return undefined
  if (accounts.length === 1) return accounts[0].accountId
  return accounts.reduce((oldest, candidate) => {
    const oldestAt = parseCreatedAt(oldest.createdAt)
    const candidateAt = parseCreatedAt(candidate.createdAt)
    if (candidateAt !== oldestAt) return candidateAt < oldestAt ? candidate : oldest
    return candidate.accountId < oldest.accountId ? candidate : oldest
  }).accountId
}

/** The single "can this account receive an order" criterion. Read-only and non-ACTIVE
 * accounts stay visible in viewing/account-management surfaces but are never order
 * targets — every order-account picker/resolver must go through this. */
export function isOrderEligibleAccount(account: {
  status: AccountStatus
  role: AccountRole
}): boolean {
  return (
    account.status === AccountStatus.ACCOUNT_STATUS_ACTIVE &&
    account.role !== AccountRole.ACCOUNT_ROLE_READ_ONLY
  )
}

export function accountsForVenue<T extends { tradeVenue: TradeVenue; accountId: string }>(
  accounts: T[],
  tradeVenue: TradeVenue | undefined
): T[] {
  if (tradeVenue === undefined) return []
  return accounts.filter((a) => a.tradeVenue === tradeVenue)
}

/**
 * Groups accounts by their own primary exchange, preserving first-seen order —
 * for any picker whose account choice can span more than one venue (Arb builder's
 * per-leg account, Hedge's dialog account, a preset's account override), where a
 * single `ExchangeAccountTabBar` isn't enough and the caller instead renders one
 * tab-bar row per venue that has an eligible account.
 */
export function groupAccountsByExchange<T extends { tradeVenue: TradeVenue }>(
  accounts: T[]
): [Exchange, T[]][] {
  const groups = new Map<Exchange, T[]>()
  for (const account of accounts) {
    const exchange = toExchangeEnum(tradeVenueToExchangeKey(account.tradeVenue))
    const group = groups.get(exchange) ?? []
    group.push(account)
    groups.set(exchange, group)
  }
  return Array.from(groups.entries())
}

type TabLike = { exchange: keyof typeof SUPPORTED_EXCHANGE; accountId?: string } | null | undefined

/** Viewing tier: the tab's own pinned account if it still exists (any status), else venue default. */
export function resolveTabViewingAccountId(
  tab: TabLike,
  accounts: PublicAccount[]
): string | undefined {
  if (!tab) return undefined
  const venueAccounts = accountsForVenue(
    accounts,
    exchangeToTradeVenue(toExchangeEnum(tab.exchange))
  )
  if (tab.accountId && venueAccounts.some((a) => a.accountId === tab.accountId))
    return tab.accountId
  return resolveDefaultAccountId(venueAccounts)
}

/** Trading tier with provenance: the tab's own pinned account ONLY if order-eligible
 * (`tab-pinned`), else the venue's stable default eligible account (`venue-default`). */
export function resolveTabTradingSelection(
  tab: TabLike,
  accounts: PublicAccount[]
): { accountId: string; source: 'tab-pinned' | 'venue-default' } | undefined {
  if (!tab) return undefined
  const eligibleVenueAccounts = accountsForVenue(
    accounts,
    exchangeToTradeVenue(toExchangeEnum(tab.exchange))
  ).filter(isOrderEligibleAccount)
  if (tab.accountId && eligibleVenueAccounts.some((a) => a.accountId === tab.accountId)) {
    return { accountId: tab.accountId, source: 'tab-pinned' }
  }
  const accountId = resolveDefaultAccountId(eligibleVenueAccounts)
  return accountId ? { accountId, source: 'venue-default' } : undefined
}

/** Trading tier: the tab's own pinned account ONLY if order-eligible, else the venue's stable default. */
export function resolveTabTradingAccountId(
  tab: TabLike,
  accounts: PublicAccount[]
): string | undefined {
  return resolveTabTradingSelection(tab, accounts)?.accountId
}

/** The venue's own default trading account, ignoring any pin — the last tier of an order
 * resolution, deliberately separate from a card's explicit/tab-pinned selection. */
export function resolveTabVenueDefaultTradingAccountId(
  tab: TabLike,
  accounts: PublicAccount[]
): string | undefined {
  if (!tab) return undefined
  return resolveDefaultAccountId(
    accountsForVenue(accounts, exchangeToTradeVenue(toExchangeEnum(tab.exchange))).filter(
      isOrderEligibleAccount
    )
  )
}

/** Picks `explicitAccountId` if it's still a valid account for `tradeVenue` (and, when
 * `activeOnly`, order-eligible), otherwise `fallback`. An explicit ChartAccountSelector pick
 * that no longer fits the card's current venue degrades to the focused-tab resolution instead
 * of silently pinning to a now-irrelevant account. */
function resolveExplicitOrFallback(
  explicitAccountId: string | null,
  tradeVenue: TradeVenue | undefined,
  accounts: PublicAccount[],
  fallback: string | undefined,
  { activeOnly }: { activeOnly: boolean }
): string | undefined {
  if (!explicitAccountId) return fallback
  const match = accountsForVenue(accounts, tradeVenue).find(
    (account) => account.accountId === explicitAccountId
  )
  if (!match) return fallback
  if (activeOnly && !isOrderEligibleAccount(match)) return fallback
  return explicitAccountId
}

export interface CardAccounts {
  tradeVenue?: TradeVenue
  /** "Viewing" identity: explicit pick → tab pin → any-status venue default. */
  viewingAccountId?: string
  /** Trading identity with derived fallbacks applied (explicit → tab pin → venue default).
   * Kept for existing consumers; do NOT feed it into an order resolution as the card tier,
   * since it always resolves and would starve `remembered`. */
  tradingAccountId?: string
  /** Only what the user (or a tab pin) actually chose for trading — never a derived default. */
  cardTradingSelection?: { accountId: string; source: 'explicit' | 'tab-pinned' }
  /** The venue's derived default trading account, ignoring any selection or pin. */
  venueDefaultTradingAccountId?: string
}

/** Pure resolution behind `useCardAccountContext`, exported so it can be tested without React. */
export function resolveCardAccounts(
  tab: TabLike,
  explicitAccountId: string | null,
  accounts: PublicAccount[]
): CardAccounts {
  const tradeVenue = tab ? exchangeToTradeVenue(toExchangeEnum(tab.exchange)) : undefined
  const tabTrading = resolveTabTradingSelection(tab, accounts)
  const explicitTrading = resolveExplicitOrFallback(
    explicitAccountId,
    tradeVenue,
    accounts,
    undefined,
    { activeOnly: true }
  )
  const cardTradingSelection = explicitTrading
    ? { accountId: explicitTrading, source: 'explicit' as const }
    : tabTrading?.source === 'tab-pinned'
      ? { accountId: tabTrading.accountId, source: 'tab-pinned' as const }
      : undefined
  return {
    tradeVenue,
    viewingAccountId: resolveExplicitOrFallback(
      explicitAccountId,
      tradeVenue,
      accounts,
      resolveTabViewingAccountId(tab, accounts),
      { activeOnly: false }
    ),
    tradingAccountId: explicitTrading ?? tabTrading?.accountId,
    cardTradingSelection,
    venueDefaultTradingAccountId: resolveTabVenueDefaultTradingAccountId(tab, accounts),
  }
}

export type ExchangeTradeability =
  | { tradeable: true }
  | { tradeable: false; reason: 'no-trade-venue' | 'no-active-account'; label: string }

/**
 * Whether `exchange` can actually receive an order right now — the check every
 * user-facing "add to Order Form" entry point (watchlist Bid/Ask, ticker-tape
 * Buy/Sell, chart plus-click, option-chain Bid/Ask) should run before accepting
 * an instrument. Two distinct failure modes, surfaced separately so callers can
 * show an accurate message instead of a generic "can't trade this":
 *
 * - `no-trade-venue`: the exchange has no account-creation venue at all (the
 *   Databento market-data venues — GLBX/XNAS/XNYS/OPRA). Permanently
 *   view/chart-only, never tradeable through this app.
 * - `no-active-account`: the venue exists, but the user has no ACTIVE,
 *   non-read-only account there yet.
 *
 * Mirrors the account filter already used for order-account pickers (see
 * useUnifiedOrderForm's `orderAccounts`) — same status/role criteria.
 */
export function resolveExchangeTradeability<
  T extends { tradeVenue: TradeVenue; status: AccountStatus; role: AccountRole },
>(exchange: Exchange, accounts: T[]): ExchangeTradeability {
  const venue = exchangeToTradeVenue(exchange)
  if (venue === undefined) {
    return { tradeable: false, reason: 'no-trade-venue', label: getExchangeLabel(exchange) }
  }
  const hasTradeableAccount = accounts.some(
    (a) => a.tradeVenue === venue && isOrderEligibleAccount(a)
  )
  return hasTradeableAccount
    ? { tradeable: true }
    : { tradeable: false, reason: 'no-active-account', label: getExchangeLabel(exchange) }
}
